import {
  eventsGoalRefused,
  goalTypeSubject,
  isEventsGoalType,
  type AgentDraft,
  type AgentDraftCreateEventsGoal,
  type AgentDraftGoal,
  type AgentDraftSessionCreateEventsGoal,
  type AgentDraftVariable,
  type EventsGoalType,
} from "@/shared/domain/agents";
import type { EventModel } from "@/shared/domain/orgs";

// ---------------------------------------------------------------------------
// Input type
//
// `createClientTool` casts raw LLM JSON to this shape with `params as T`,
// so nothing here is statically verified at runtime. Scalars are typed
// best-effort (LLMs respect string types in practice); collection fields
// are `unknown[]` because the LLM may emit goals or variables with
// missing / wrong-typed nested fields, and the normalize* functions
// below are the only validation layer between that input and the
// stashed draft.
// ---------------------------------------------------------------------------

export interface ClientCreateAgentInput {
  name?: string;
  description?: string;
  model_profile?: string;
  kickoff_message?: string;
  goals?: unknown[];
  variables?: unknown[];
  /** Names of org-shared skills to attach. Resolved to ids by `execute`. */
  skills?: unknown[];
  target_agent_name?: string;
}

// ---------------------------------------------------------------------------
// Coercion — tool input → AgentDraft
//
// This is the one validation seam between LLM-emitted JSON and the
// stashed draft the editor reads back. Treat every field as untrusted
// even where the TypeScript annotation suggests otherwise.
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function trimOrUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Drains a wire-format vocabulary into a trimmed-key ``Record``, dropping
 * empty or non-string keys and non-string descriptions. Every goal vocabulary
 * (triage labels, event types, tags) goes through this one rule.
 */
function coerceVocabulary(raw: unknown): Record<string, string> {
  const vocab: Record<string, string> = {};
  if (!isPlainObject(raw)) {
    return vocab;
  }
  for (const [key, description] of Object.entries(raw)) {
    const trimmedKey = key.trim();
    if (trimmedKey === "" || typeof description !== "string") {
      continue;
    }
    vocab[trimmedKey] = description;
  }
  return vocab;
}

function normalizeEventsGoal(
  raw: Record<string, unknown>,
  goalType: EventsGoalType,
): AgentDraftCreateEventsGoal | AgentDraftSessionCreateEventsGoal | null {
  const eventVocab = coerceVocabulary(raw.event_vocabulary);
  // Drop an events goal whose event vocabulary ended up empty after
  // filtering — same rationale as the triage branch: the schema marks
  // `event_vocabulary` as required for the type and the editor's validator
  // would block save until the user adds at least one row.
  if (Object.keys(eventVocab).length === 0) {
    return null;
  }
  const goal: AgentDraftCreateEventsGoal | AgentDraftSessionCreateEventsGoal = {
    goal_type: goalType,
    event_vocabulary: eventVocab,
  };
  const tagVocab = coerceVocabulary(raw.tag_vocabulary);
  if (Object.keys(tagVocab).length > 0) {
    goal.tag_vocabulary = tagVocab;
  }
  if (
    goal.goal_type === "create_events" &&
    typeof raw.collection_enabled === "boolean"
  ) {
    goal.collection_enabled = raw.collection_enabled;
  }
  const focus = trimOrUndefined(raw.event_focus_prompt);
  if (focus !== undefined) {
    goal.event_focus_prompt = focus;
  }
  return goal;
}

function normalizeGoals(
  goals: unknown[] | undefined,
): AgentDraftGoal[] | undefined {
  if (!Array.isArray(goals) || goals.length === 0) {
    return undefined;
  }
  const normalized: AgentDraftGoal[] = [];
  for (const raw of goals) {
    if (!isPlainObject(raw)) {
      continue;
    }
    const goalType = raw.goal_type;
    if (goalType === "dataset_summary" || goalType === "session_summary") {
      const prompt = trimOrUndefined(raw.summary_format_spec_prompt);
      normalized.push(
        prompt === undefined
          ? { goal_type: goalType }
          : { goal_type: goalType, summary_format_spec_prompt: prompt },
      );
    } else if (goalType === "dataset_triage" || goalType === "session_triage") {
      const vocab = coerceVocabulary(raw.label_vocabulary);
      // Drop a triage goal whose vocabulary ended up empty after
      // filtering — schema marks `label_vocabulary` as required for the
      // type, and the editor's validator would block save until the
      // user adds at least one row. Better to surface nothing than a
      // half-formed goal that looks like it came from the model.
      if (Object.keys(vocab).length > 0) {
        normalized.push({ goal_type: goalType, label_vocabulary: vocab });
      }
    } else if (isEventsGoalType(goalType)) {
      const goal = normalizeEventsGoal(raw, goalType);
      if (goal !== null) {
        normalized.push(goal);
      }
    }
    // Unknown goal_type values are dropped silently so a future server
    // goal we don't know about can't wedge the editor.
  }
  return normalized.length === 0 ? undefined : normalized;
}

function normalizeVariables(
  variables: unknown[] | undefined,
): AgentDraftVariable[] | undefined {
  if (!Array.isArray(variables) || variables.length === 0) {
    return undefined;
  }
  const normalized: AgentDraftVariable[] = [];
  for (const raw of variables) {
    if (!isPlainObject(raw)) {
      continue;
    }
    const name = trimOrUndefined(raw.name);
    if (name === undefined) {
      continue;
    }
    const entry: AgentDraftVariable = { name };
    const description = trimOrUndefined(raw.description);
    if (description !== undefined) {
      entry.description = description;
    }
    // `default` is preserved verbatim (including empty / whitespace-only
    // strings) because an empty default is a meaningful choice — the LLM
    // is telling us "this variable has no default; force the caller to
    // supply one." Trimming here would collapse that into "no default
    // declared", which is a different (less strict) contract.
    if (typeof raw.default === "string") {
      entry.default = raw.default;
    }
    normalized.push(entry);
  }
  return normalized.length === 0 ? undefined : normalized;
}

export function coerceToAgentDraft(
  input: ClientCreateAgentInput,
): AgentDraft | null {
  const name = trimOrUndefined(input.name);
  // Name is required to CREATE, but optional to UPDATE: an update handoff
  // (target_agent_name set) that omits `name` means "keep the saved
  // agent's name", so the draft may legitimately carry no name at all.
  // The resolved `target_agent_id` is attached by `execute` after the
  // name → id lookup — it isn't known here.
  const isUpdate = trimOrUndefined(input.target_agent_name) !== undefined;
  if (name === undefined && !isUpdate) {
    return null;
  }
  const draft: AgentDraft = {};
  if (name !== undefined) {
    draft.name = name;
  }
  const description = trimOrUndefined(input.description);
  if (description !== undefined) {
    draft.description = description;
  }
  const modelProfile = trimOrUndefined(input.model_profile);
  if (modelProfile !== undefined) {
    draft.model_profile = modelProfile;
  }
  if (
    typeof input.kickoff_message === "string" &&
    input.kickoff_message.trim() !== ""
  ) {
    // Preserve interior whitespace verbatim — kickoff messages routinely
    // span lines, and `\n\n` between paragraphs is meaningful. The trim
    // check above only filters out the all-whitespace case, which
    // produces a useless empty user-turn in the editor.
    draft.kickoff_message = input.kickoff_message;
  }
  const goals = normalizeGoals(input.goals);
  if (goals !== undefined) {
    draft.goals = goals;
  }
  const variables = normalizeVariables(input.variables);
  if (variables !== undefined) {
    draft.variables = variables;
  }
  return draft;
}

/**
 * Why an org on `eventModel` cannot take the events goals `input` proposes,
 * worded for the model to act on, or `undefined` when it can (or when the
 * event model is not known yet, which leaves the decision to the server).
 *
 * The editor will not save an events goal the org's event model does not
 * allow, and the server refuses one when the agent is launched, so proposing
 * one would hand the user a draft they cannot save.
 */
export function eventsGoalRefusalFor(
  input: ClientCreateAgentInput,
  eventModel: EventModel | undefined,
): string | undefined {
  if (eventModel === undefined) {
    return undefined;
  }
  const refused = (normalizeGoals(input.goals) ?? []).find((goal) =>
    eventsGoalRefused(goal.goal_type, eventModel),
  );
  if (refused === undefined) {
    return undefined;
  }
  return refused.goal_type === "create_events"
    ? `This organization uses session events (event model "${eventModel}"), so ` +
        "an agent cannot create dataset events with a create_events goal. Use a " +
        "session_create_events goal instead (same event_vocabulary, " +
        "tag_vocabulary and event_focus_prompt, no collection_enabled); the " +
        "session is chosen when the agent is launched."
    : `This organization does not have session events (event model "${eventModel}"), ` +
        "so an agent cannot create them with a session_create_events goal. Use a " +
        "create_events goal instead; the dataset is chosen when the agent is launched.";
}

/**
 * Why `input` cannot be one agent because it mixes goals that work on a
 * dataset with goals that work on a session, worded for the model to act on,
 * or `undefined` when its goals all work on one kind of record. The server
 * refuses such an agent when it is saved.
 */
export function mixedSubjectRefusalFor(
  input: ClientCreateAgentInput,
): string | undefined {
  const goals = normalizeGoals(input.goals) ?? [];
  const onDataset = goals.filter(
    (goal) => goalTypeSubject(goal.goal_type) === "dataset",
  );
  const onSession = goals.filter(
    (goal) => goalTypeSubject(goal.goal_type) === "session",
  );
  if (onDataset.length === 0 || onSession.length === 0) {
    return undefined;
  }
  const typesOf = (some: AgentDraftGoal[]) =>
    [...new Set(some.map((goal) => goal.goal_type))].join(", ");
  return (
    "An agent runs on either a dataset or a session, so its goals cannot mix " +
    `dataset goals (${typesOf(onDataset)}) with session goals ` +
    `(${typesOf(onSession)}). Propose the goals for one of the two, using ` +
    "the counterpart goal types (dataset_summary/session_summary, " +
    "dataset_triage/session_triage, create_events/session_create_events), " +
    "or propose two separate agents."
  );
}

/**
 * What a `client_create_agent` proposal will actually do, for the approval
 * preview to render.
 *
 * The preview's whole job is to show the consequence of approving, so it must
 * read the same normalized values `execute` stashes rather than the raw model
 * params: `normalizeGoals` and `normalizeVariables` drop entries the model
 * emitted incompletely, and a preview built from raw params would list goals
 * and count variables that never reach the editor.
 *
 * `targetAgentName` also decides create-vs-update here, so that predicate has
 * one definition shared with `execute` instead of a copy per caller.
 */
export function previewAgentProposal(input: ClientCreateAgentInput): {
  targetAgentName: string | undefined;
  draft: AgentDraft | null;
  skillNames: string[] | undefined;
} {
  return {
    targetAgentName: trimOrUndefined(input.target_agent_name),
    draft: coerceToAgentDraft(input),
    skillNames: normalizeSkillNames(input.skills),
  };
}

/**
 * Skill names the model asked to attach, de-duplicated and trimmed, or
 * `undefined` when the field was omitted.
 *
 * An explicitly-empty list survives as `[]` — "detach every skill" is a
 * meaningful instruction and must not collapse into "field omitted", which
 * would leave the existing skills attached.
 */
export function normalizeSkillNames(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const seen = new Set<string>();
  const names: string[] = [];
  for (const value of raw) {
    const name = trimOrUndefined(value);
    if (name === undefined || seen.has(name)) {
      continue;
    }
    seen.add(name);
    names.push(name);
  }
  return names;
}
